import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type FeatureMap = { [key: string]: boolean };

const STORAGE_KEY = 'twin_features';

/**
 * FeatureService — single source of truth for tenant feature flags in the
 * Angular app. Fetches the flag map from /api/me/features once at app
 * startup, caches it in localStorage so the next page load is synchronous,
 * and exposes has() for *ngIf and FeatureGuard.
 */
@Injectable({ providedIn: 'root' })
export class FeatureService {

  private flags: FeatureMap = {};
  private loaded = false;

  /** Emits whenever the flag map is (re)loaded or cleared, for UI built once rather than per check (the sidebar menu). */
  readonly changes$ = new Subject<void>();

  constructor(private http: HttpClient) {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        this.flags = JSON.parse(cached) || {};
        this.loaded = true;
      } catch (e) {
        this.flags = {};
      }
    }
  }

  /**
   * Synchronous flag check. Templates use this with *ngIf — e.g.
   * *ngIf="features.has('inspection.bidding')".
   *
   * Behavior:
   * - If the flag map hasn't loaded yet (first request after login, API
   *   unreachable, fresh tab with no cache): return TRUE. Matches the
   *   "default = all on, admin removes" spec — an unloaded state means no
   *   admin override applied yet, so the user sees everything. Avoids the
   *   empty-sidebar failure mode.
   * - Once loaded: return the actual stored value. Unknown keys → FALSE.
   */
  has(key: string): boolean {
    if (!this.loaded) {
      return true;
    }
    return !!this.flags[key];
  }

  /**
   * True once the flag map has been fetched from the API at least once
   * in this session (or restored from cache on page load).
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Refresh the flag map from the API. Call this after login and any time
   * a stale cache is suspected. Replaces the in-memory map and persists
   * to localStorage so subsequent reloads start synchronous.
   */
  refresh(): Observable<FeatureMap> {
    const url = `${environment.api}me/features`;
    return this.http.get<FeatureMap>(url).pipe(
      tap(map => {
        this.flags = map || {};
        this.loaded = true;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.flags));
        this.changes$.next();
        const enabledCount = Object.values(this.flags).filter(v => v).length;
        const totalCount = Object.keys(this.flags).length;
        console.log(`[FeatureService] Loaded ${enabledCount}/${totalCount} flags from ${url}`);
      }),
      catchError(err => {
        // Mark loaded=true so the default-true fallback in has() doesn't
        // mask the issue indefinitely — if there's no flag map, the
        // sidebar should show that state (empty) so it's diagnosable.
        // We still return whatever cached map we had (may be empty).
        this.loaded = true;
        this.changes$.next();
        console.error(`[FeatureService] Failed to load flags from ${url}`, err);
        return of(this.flags);
      }),
    );
  }

  /**
   * Drop the cached map. Use on logout so the next user starts fresh.
   */
  clear(): void {
    this.flags = {};
    this.loaded = false;
    localStorage.removeItem(STORAGE_KEY);
    this.changes$.next();
  }
}
